import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const AdminEvents = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', date: '', venue: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  // Quota
  const [quota, setQuota] = useState(null);
  const [showBuy, setShowBuy] = useState(false);
  const [packages, setPackages] = useState([]);
  const [payLoading, setPayLoading] = useState(null);
  const [payMsg, setPayMsg] = useState('');

  const loadEvents = async () => {
    try {
      const [eventsRes, meRes] = await Promise.all([
        axios.get('/api/admin/events'),
        axios.get('/api/admin/auth/me')
      ]);
      setEvents(eventsRes.data);
      setQuota(meRes.data.remaining_quota ?? null);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) logout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadEvents(); }, []);

  const openBuyModal = async () => {
    setPayMsg('');
    setShowBuy(true);
    if (packages.length === 0) {
      try {
        const res = await axios.get('/api/packages?type=photographer');
        setPackages(res.data);
      } catch {}
    }
  };

  const buyPackage = async (pkg) => {
    setPayLoading(pkg.id);
    setPayMsg('');
    try {
      const res = await axios.post('/api/payments/create-order', { package_id: pkg.id });
      const { razorpay_order_id, amount, key_id, package_name, events_count } = res.data;

      if (!window.Razorpay) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.onload = resolve; script.onerror = reject;
          document.body.appendChild(script);
        });
      }

      const rzp = new window.Razorpay({
        key: key_id,
        amount,
        currency: 'INR',
        order_id: razorpay_order_id,
        name: 'Event Photos',
        description: `${package_name} — ${events_count} event credits`,
        prefill: { name: admin?.name, email: admin?.email },
        handler: () => {
          setPayMsg(`Payment successful! ${events_count} event credits added to your account.`);
          setShowBuy(false);
          // Refresh quota after short delay for webhook to process
          setTimeout(async () => {
            try {
              const meRes = await axios.get('/api/admin/auth/me');
              setQuota(meRes.data.remaining_quota ?? null);
            } catch {}
          }, 3000);
        },
        modal: { ondismiss: () => setPayMsg('') }
      });
      rzp.open();
    } catch (err) {
      setPayMsg(err.response?.data?.error || 'Payment failed. Please try again.');
    } finally {
      setPayLoading(null);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError('');
    setCreating(true);
    try {
      const res = await axios.post('/api/admin/events', form);
      setEvents(prev => [res.data, ...prev]);
      setShowCreate(false);
      setForm({ name: '', date: '', venue: '', description: '' });
      navigate(`/admin/events/${res.data.id}`);
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create event.');
      if (err.response?.data?.quota_empty) {
        setShowCreate(false);
        openBuyModal();
      }
    } finally {
      setCreating(false);
    }
  };

  const statusColors = {
    active: { bg: '#f0fdf4', color: '#166534', label: 'Active' },
    matching: { bg: '#fef9c3', color: '#854d0e', label: 'Processing' },
    completed: { bg: '#eff6ff', color: '#1e40af', label: 'Completed' }
  };

  return (
    <div style={styles.page}>
      {/* Buy Credits Modal */}
      {showBuy && (
        <div style={styles.modalOverlay} onClick={() => setShowBuy(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Buy Event Credits</h2>
            <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              Current balance: <strong style={{ color: '#1a1a2e' }}>{quota} credits</strong>
            </p>
            {packages.length === 0 ? (
              <p style={{ color: '#aaa', textAlign: 'center' }}>Loading packages...</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {packages.map(pkg => (
                  <div key={pkg.id} style={styles.pkgRow}>
                    <div>
                      <div style={styles.pkgName}>{pkg.name}</div>
                      <div style={styles.pkgMeta}>{pkg.events_count} event credits</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={styles.pkgPrice}>₹{(pkg.price_paise / 100).toLocaleString('en-IN')}</span>
                      <button
                        style={{ ...styles.submitBtn, ...(payLoading === pkg.id ? styles.disabled : {}) }}
                        disabled={!!payLoading}
                        onClick={() => buyPackage(pkg)}
                      >
                        {payLoading === pkg.id ? 'Processing...' : 'Buy'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {payMsg && <div style={{ ...styles.error, background: payMsg.includes('successful') ? '#f0fdf4' : '#fef2f2', color: payMsg.includes('successful') ? '#166534' : '#dc2626', border: payMsg.includes('successful') ? '1px solid #bbf7d0' : '1px solid #fecaca', marginTop: '1rem' }}>{payMsg}</div>}
            <button style={{ ...styles.cancelBtn, marginTop: '1rem', width: '100%' }} onClick={() => setShowBuy(false)}>Close</button>
          </div>
        </div>
      )}

      <div style={styles.container}>
        {/* Header */}
        <div style={styles.pageHeader}>
          <h1 style={styles.pageTitle}>Your Events</h1>
          <button style={styles.createBtn} onClick={() => setShowCreate(true)}>
            + Create Event
          </button>
        </div>

        {/* Create Event Modal */}
        {showCreate && (
          <div style={styles.modalOverlay} onClick={() => setShowCreate(false)}>
            <div style={styles.modal} onClick={e => e.stopPropagation()}>
              <h2 style={styles.modalTitle}>Create New Event</h2>
              <form onSubmit={handleCreate} style={styles.form}>
                <Field label="Event Name" required>
                  <input style={styles.input} placeholder="e.g. Smith & Jones Wedding" value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
                </Field>
                <Field label="Event Date" required>
                  <input type="date" style={styles.input} value={form.date}
                    onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required />
                </Field>
                <Field label="Venue" required>
                  <input style={styles.input} placeholder="e.g. The Grand Ballroom, Mumbai" value={form.venue}
                    onChange={e => setForm(f => ({ ...f, venue: e.target.value }))} required />
                </Field>
                <Field label="Description (optional)">
                  <textarea style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }}
                    placeholder="Optional event description"
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                </Field>
                {createError && <div style={styles.error}>{createError}</div>}
                <div style={styles.modalActions}>
                  <button type="button" style={styles.cancelBtn} onClick={() => setShowCreate(false)}>Cancel</button>
                  <button type="submit" style={{ ...styles.submitBtn, ...(creating ? styles.disabled : {}) }} disabled={creating}>
                    {creating ? 'Creating...' : 'Create Event'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Events Grid */}
        {loading ? (
          <div style={styles.empty}>Loading events...</div>
        ) : events.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</div>
            <p style={{ color: '#888' }}>No events yet. Create your first event to get started.</p>
          </div>
        ) : (
          <div style={styles.grid}>
            {events.map(event => {
              const sc = statusColors[event.status] || statusColors.active;
              return (
                <div key={event.id} style={styles.eventCard} onClick={() => navigate(`/admin/events/${event.id}`)}>
                  <div style={styles.cardHeader}>
                    <h3 style={styles.eventName}>{event.name}</h3>
                    <span style={{ ...styles.statusBadge, background: sc.bg, color: sc.color }}>{sc.label}</span>
                  </div>
                  <p style={styles.eventMeta}>
                    📅 {new Date(event.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                  <p style={styles.eventMeta}>📍 {event.venue}</p>
                  <div style={styles.cardStats}>
                    <Stat label="Visitors" value={event.visitor_count || 0} />
                    <Stat label="Photos" value={event.photo_count || 0} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <div style={{ marginBottom: '1rem' }}>
    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>
      {label}{required && ' *'}
    </label>
    {children}
  </div>
);

const Stat = ({ label, value }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#1a1a2e' }}>{value}</div>
    <div style={{ fontSize: '0.75rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
  </div>
);

const styles = {
  page: { minHeight: '100vh', background: '#f8fafc', fontFamily: "'Segoe UI', system-ui, sans-serif" },
  nav: { background: '#1a1a2e', padding: '1rem 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  navBrand: { color: '#fff', fontWeight: '700', fontSize: '1.1rem' },
  navRight: { display: 'flex', alignItems: 'center', gap: '1rem' },
  navUser: { color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' },
  logoutBtn: { background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.4rem 0.875rem', cursor: 'pointer', fontSize: '0.875rem' },
  quotaBadge: { background: 'rgba(255,255,255,0.12)', borderRadius: '0.5rem', padding: '0.3rem 0.75rem', display: 'flex', alignItems: 'center' },
  quotaNum: { color: '#fff', fontWeight: '800', fontSize: '1rem' },
  quotaLabel: { color: 'rgba(255,255,255,0.65)', fontSize: '0.8rem' },
  buyBtn: { background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.4rem 0.875rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '700' },
  pkgRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', borderRadius: '0.75rem', padding: '0.875rem 1rem', border: '1.5px solid #e5e7eb' },
  pkgName: { fontWeight: '700', color: '#1a1a2e', fontSize: '0.95rem' },
  pkgMeta: { color: '#888', fontSize: '0.8rem', marginTop: '0.15rem' },
  pkgPrice: { fontWeight: '800', color: '#1a1a2e', fontSize: '1.05rem' },
  container: { maxWidth: '1100px', margin: '0 auto', padding: '2rem 1.5rem' },
  pageHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem' },
  pageTitle: { fontSize: '1.75rem', fontWeight: '800', color: '#1a1a2e', margin: 0 },
  createBtn: { background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '0.75rem', padding: '0.7rem 1.25rem', fontWeight: '700', cursor: 'pointer', fontSize: '0.95rem' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.25rem' },
  eventCard: { background: '#fff', borderRadius: '1.25rem', padding: '1.5rem', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', transition: 'box-shadow 0.2s, transform 0.2s', border: '1.5px solid #f0f0f0' },
  cardHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.75rem' },
  eventName: { fontSize: '1.05rem', fontWeight: '700', color: '#1a1a2e', margin: 0, flex: 1 },
  statusBadge: { borderRadius: '50px', padding: '0.2rem 0.7rem', fontSize: '0.75rem', fontWeight: '600', whiteSpace: 'nowrap' },
  eventMeta: { fontSize: '0.85rem', color: '#888', margin: '0.25rem 0' },
  cardStats: { display: 'flex', gap: '1.5rem', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #f0f0f0' },
  empty: { textAlign: 'center', padding: '3rem', color: '#888' },
  emptyState: { textAlign: 'center', padding: '4rem', background: '#fff', borderRadius: '1.5rem' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' },
  modal: { background: '#fff', borderRadius: '1.5rem', padding: '2rem', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { fontSize: '1.4rem', fontWeight: '800', color: '#1a1a2e', margin: '0 0 1.5rem' },
  form: {},
  input: { width: '100%', padding: '0.75rem 1rem', border: '1.5px solid #e5e7eb', borderRadius: '0.75rem', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' },
  error: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', padding: '0.75rem', color: '#dc2626', fontSize: '0.875rem', marginBottom: '1rem' },
  modalActions: { display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' },
  cancelBtn: { padding: '0.7rem 1.25rem', background: '#f3f4f6', border: 'none', borderRadius: '0.75rem', cursor: 'pointer', fontWeight: '600', color: '#444' },
  submitBtn: { padding: '0.7rem 1.5rem', background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '0.75rem', fontWeight: '700', cursor: 'pointer' },
  disabled: { opacity: 0.6, cursor: 'not-allowed' }
};

export default AdminEvents;
